import { useTranslation } from "react-i18next";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { FooterButtons } from "src/common/components/FooterButtons";
import { ModulesSelectedForPublication } from "./ReadOnlyValidationComponents/ModulesSelectedForPublication";
import { PlatformsSelectedForPublication } from "./ReadOnlyValidationComponents/PlatformsSelectedForPublication";
import { SourceAppMainMenuMedia360BackgroundSelectedForPublication } from "./ReadOnlyValidationComponents/SourceAppMainMenuMedia360BackgroundSelectedForPublication";
import {
  askPublication,
  getLatestBuildsOfProjectGroup,
  getNewPublicationSettings,
  IAskPublicationPayload,
} from "src/features/builds/state/buildsSlice";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getSelectedProjectGroup } from "src/features/projectGroups/state/projectGroupsSlice";
import { mediaSelector } from "src/features/gallery/state/gallerySlice";
import { useAppDispatch } from "src/common/state/hooks";
import { setAlert } from "src/features/alert/state/alertsSlice";
import ThemePreview from "src/common/components/ThemePreview/ThemePreview";
import InformationFrame from "src/common/components/InfomationFrame/InformationFrame";
import { getPlatformBuildInfo } from "src/common/util/getPlatformBuildInfo";
import { dateConfig } from "src/common/util/DateConfig";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import { ILanguage } from "src/model/model";
import { languageConfig } from "src/common/util/langConfig";
import { getThemes } from "src/features/themes/state/themeSlice";

type Props = {
  incrementStep?(): void;
  decrementStep?(): void;
};

export const CONFIRMATION_BEFORE_PUBLISH_STEP_COMPONENT_ID =
  "confirmationBeforePublishStepComponentId";

const AUTH_TYPE_LABELS: Record<string, string> = {
  classic: "Classique",
  simplified: "Simplifié",
  none: "Anonyme",
};

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return "—";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} Mo`;
}

export default function ConfirmationBeforePublishStep({ decrementStep }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const newPublicationSettings = useSelector(getNewPublicationSettings);
  const relevantProjectGroup = useSelector(getSelectedProjectGroup);
  const sourceMedias = useSelector(mediaSelector);
  const selectedPlatforms = useSelector(getNewPublicationSettings).selectedPlatforms;
  const relevantPgThumbnail = relevantProjectGroup?.source_thumbnail;
  const default360BackgroundMenuImage = sourceMedias.find((sm) => Number(sm.id) === 5346);
  const latestBuildsOfProjectGroup = useSelector(
    getLatestBuildsOfProjectGroup(relevantProjectGroup.id),
  );
  const latestBuild = latestBuildsOfProjectGroup[0];
  const currentUser = useSelector(getCurrentUser);
  const preferredLanguage = languageConfig((currentUser?.preferred_language as ILanguage)?.name);

  const allThemes = useSelector(getThemes);
  const rawThemeInfo = relevantProjectGroup?.theme_info;
  const themeId = typeof rawThemeInfo === "object" ? rawThemeInfo?.id : rawThemeInfo;
  const themeInfo =
    (typeof rawThemeInfo === "object" && rawThemeInfo?.name ? rawThemeInfo : null) ||
    allThemes.find((t) => Number(t.id) === Number(themeId)) ||
    null;

  const timeEstimate = selectedPlatforms.reduce((acc, platformId) => {
    const info = getPlatformBuildInfo(platformId);
    return info ? acc + info.buildDurationInMin : acc;
  }, 0);

  const nextVersion = latestBuild ? latestBuild.version + 1 : 1;
  const displayVersion = newPublicationSettings.isNewVersion
    ? `V${nextVersion}`
    : latestBuild
    ? `V${latestBuild.version}`
    : "V1";

  const navigateToPublishView = () => {
    navigate(`/courses/${relevantProjectGroup.id}/publish`);
  };

  const handleSubmit = () => {
    const payload: IAskPublicationPayload = {
      projectGroupId: relevantProjectGroup.id,
      selectedProjectsIds: [],
      selectedPlatforms,
      shouldBeTested: 0,
      isNewVersion: false,
      callback: navigateToPublishView,
    };

    if (!newPublicationSettings.isNewVersion) {
      payload.selectedProjectsIds =
        latestBuild?.project_group_snapshot_sorted_by_order?.published_projects?.map(
          (p) => p.project.id,
        ) ?? [];
      payload.shouldBeTested = latestBuild?.should_be_tested ? 1 : 0;
    } else {
      payload.selectedProjectsIds = newPublicationSettings.selectedProjectsIds;
      payload.shouldBeTested = newPublicationSettings.shouldBeTested;
      payload.isNewVersion = newPublicationSettings.isNewVersion;
    }

    dispatch(
      askPublication({ ...payload, componentId: CONFIRMATION_BEFORE_PUBLISH_STEP_COMPONENT_ID }),
    )
      .then(() => {
        dispatch(
          setAlert({
            type: "success",
            msg: t("alerts.requestSent"),
            subMsg: t("alerts.publicationSuccess"),
            customTimeoutInMs: 30000,
          }),
        );
        navigateToPublishView();
      })
      .catch(() => {
        dispatch(setAlert({ type: "danger", msg: t("alerts.publicationFail") }));
      });
  };

  const totalWeight = relevantProjectGroup?.project_group_elements?.reduce(
    (acc: number, pge: any) => {
      const projectWeight = pge?.project?.size_in_bytes || pge?.size_in_bytes || 0;
      return acc + projectWeight;
    },
    0,
  );

  return (
    <div className="flex flex-col grow w-full h-0">
      <IsLoading
        componentId={CONFIRMATION_BEFORE_PUBLISH_STEP_COMPONENT_ID}
        showSuccess={false}
        spinnerPlaceholder
      >
        <div className="flex flex-col grow w-full h-0">
          {/* Content area — no page scroll, fills viewport */}
          <div className="flex flex-col grow min-h-0 px-4 py-2">
            <div className="w-full max-w-6xl mx-auto flex flex-col grow min-h-0 gap-3">
              {/* === HEADER BAR: Course identity + metadata === */}
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 flex-shrink-0">
                <div className="flex flex-wrap items-start gap-x-6 gap-y-1 text-sm">
                  {/* Thumbnail */}
                  <div>
                    <span className="text-gray-400 text-xs">
                      {t("pages.projectGroup.publish.confirmationBeforePublish.selectedThumbnail")}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {relevantPgThumbnail?.s3_url ? (
                        <div className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden bg-black ring-1 ring-gray-200">
                          <img
                            src={relevantPgThumbnail.s3_url}
                            alt="Thumbnail"
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="w-8 h-8 flex-shrink-0 rounded-md border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400 text-xs">
                          —
                        </div>
                      )}
                      {(() => {
                        const fullName = relevantPgThumbnail?.filename || relevantProjectGroup.name;
                        const lastDot = fullName.lastIndexOf(".");
                        const stem = lastDot > 0 ? fullName.slice(0, lastDot) : fullName;
                        const ext = lastDot > 0 ? fullName.slice(lastDot) : "";
                        const maxStem = 10;
                        const cropped = stem.length > maxStem ? stem.slice(0, maxStem) + "…" : stem;
                        return (
                          <p className="font-semibold text-gray-800 text-sm" title={fullName}>
                            {cropped}
                            {ext}
                          </p>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="w-px self-stretch bg-gray-200 hidden lg:block" />

                  <div>
                    <span className="text-gray-400 text-xs">{t("general.coursesName")}</span>
                    <p className="font-semibold text-gray-800 truncate mt-2">
                      {relevantProjectGroup.name}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">
                      {t("pages.projectGroup.publish.confirmationBeforePublish.version")}
                    </span>
                    <p className="font-semibold text-gray-800 mt-2">{displayVersion}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">{t("general.authenticationType")}</span>
                    <p className="font-semibold text-gray-800 mt-2">
                      {AUTH_TYPE_LABELS[relevantProjectGroup.auth_type] ||
                        relevantProjectGroup.auth_type}
                    </p>
                  </div>
                  {totalWeight > 0 && (
                    <div>
                      <span className="text-gray-400 text-xs">{t("general.weight")}</span>
                      <p className="font-semibold text-gray-800 mt-2">{formatBytes(totalWeight)}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-400 text-xs">{t("general.createdAt")}</span>
                    <p className="font-semibold text-gray-800 mt-2">
                      {relevantProjectGroup.createdAt
                        ? new Date(relevantProjectGroup.createdAt).toLocaleDateString(
                            dateConfig(preferredLanguage),
                          )
                        : "—"}
                    </p>
                  </div>

                  <div className="w-px self-stretch bg-gray-200 hidden lg:block" />

                  {/* Theme */}
                  <div>
                    <span className="text-gray-400 text-xs">
                      {t("pages.projectGroup.publish.confirmationBeforePublish.courseTheme")}
                    </span>
                    {themeInfo ? (
                      <div className="flex items-center gap-1.5">
                        <div className="w-8 h-8 flex-shrink-0">
                          <ThemePreview themeInfo={themeInfo} className="w-full h-full" />
                        </div>
                        <p className="font-semibold text-gray-800 text-sm truncate">
                          {themeInfo.name || "—"}
                        </p>
                      </div>
                    ) : (
                      <p className="font-semibold text-gray-800">
                        {t("pages.projectGroup.publish.confirmationBeforePublish.noTheme")}
                      </p>
                    )}
                  </div>

                  {/* Time estimate */}
                  {timeEstimate > 0 && (
                    <>
                      <div className="w-px self-stretch bg-gray-200 hidden lg:block" />
                      <div>
                        <span className="text-gray-400 text-xs">
                          {t("pages.projectGroup.publish.publishDuration").replace(/\s*:\s*$/, "")}
                        </span>
                        <p className="font-semibold text-gray-800 mt-2">
                          ~{timeEstimate} {t("pages.projectGroup.publish.minutes")}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* === FROZEN VERSION WARNING — same-version republication === */}
              {!newPublicationSettings.isNewVersion &&
                latestBuild &&
                (() => {
                  const firstPublishTs = latestBuildsOfProjectGroup.reduce(
                    (min: number, b) =>
                      b.request_date_ts && b.request_date_ts < min ? b.request_date_ts : min,
                    latestBuild.request_date_ts ?? Infinity,
                  );
                  const firstPublishDate =
                    firstPublishTs !== Infinity
                      ? new Date(firstPublishTs * 1000).toLocaleDateString(
                          dateConfig(preferredLanguage),
                          { year: "numeric", month: "long", day: "numeric" },
                        )
                      : null;
                  return (
                    <InformationFrame
                      style="warning"
                      showCloseButton={false}
                      withTimeout={false}
                      innerClassName="flex p-3 rounded-lg"
                    >
                      <span className="text-sm">
                        {t("pages.projectGroup.publish.frozenVersionWarning", {
                          date: firstPublishDate ?? "",
                        })}
                      </span>
                    </InformationFrame>
                  );
                })()}

              {/* === MAIN CONTENT: 2-column layout — fills remaining space === */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 flex-1 min-h-0">
                {/* LEFT: 360 background image — 2/5 */}
                <div className="lg:col-span-2 min-h-0">
                  <SourceAppMainMenuMedia360BackgroundSelectedForPublication
                    media={relevantProjectGroup.source_app_main_menu_media}
                    defaultMediaUrl={default360BackgroundMenuImage?.s3_url}
                    defaultMediaName={
                      default360BackgroundMenuImage?.filename || default360BackgroundMenuImage?.name
                    }
                  />
                </div>

                {/* RIGHT: Modules + Platforms stacked — 3/5 */}
                <div className="lg:col-span-3 flex flex-col gap-3 min-h-0">
                  <div className="flex-1 min-h-0">
                    <ModulesSelectedForPublication
                      selectedProjectIds={
                        !newPublicationSettings.isNewVersion && latestBuild
                          ? latestBuild.project_group_snapshot_sorted_by_order.published_projects.map(
                              (p) => p.project.id,
                            )
                          : newPublicationSettings.selectedProjectsIds
                      }
                    />
                  </div>
                  <div className="flex-shrink-0">
                    <PlatformsSelectedForPublication
                      selectedPlatforms={newPublicationSettings.selectedPlatforms}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer — always pinned at bottom */}
          <div style={{ height: "4.1875rem" }} className="flex-shrink-0">
            <FooterButtons
              cancelText={t("general.cancel")}
              cancelCb={() => navigateToPublishView && navigateToPublishView()}
              backText={t("general.back")}
              backCb={!newPublicationSettings.isNewVersion ? navigateToPublishView : decrementStep}
              nextText={t("general.publish")}
              nextCb={() => handleSubmit()}
            />
          </div>
        </div>
      </IsLoading>
    </div>
  );
}
